import { toast } from 'sonner';
import { create } from 'zustand';

import ArrowForwardIcon from 'DesignSystem2/Icon/material/ArrowForward';
import SizedIcon from 'DesignSystem2/Icon/SizedIcon';
import type { ServerError } from 'models';
import type { SnackAction, SnackMessage } from 'models/snackbar';
import { isSuppressedApiError } from 'transport/core';
import { sendEvent as sendAmplitudeEvent } from 'utils/analytics/providers/amplitude';
import { upperFirstLetter } from 'utils/caseing';
import { serverErrorSnack } from 'utils/errors';
import logProviderFactory from 'utils/logger/logProviderFactory';

interface SnackbarActions {
    pushMessage: (message: SnackMessage, action?: SnackAction) => string | number | undefined;
    popMessage: (toastId?: string | number) => void;
}

interface SnackbarState {
    actions: SnackbarActions;
}

let lastPushId: string | number | undefined;

export const READ_ONLY_MESSAGE = 'Your account has read-only access and cannot manage orders.';

/** Route each variant to the matching sonner type so it picks up the right
 * semantic icon/accent configured in `AppToaster`. */
function toastFnForVariant(variant: SnackMessage['variant']): typeof toast.info {
    if (variant === 'positive') {
        return toast.success;
    }
    if (variant === 'negative') {
        return toast.error;
    }
    return toast.info;
}

export const useSnackbarStore = create<SnackbarState>()(() => ({
    actions: {
        pushMessage: (message: SnackMessage, action?: SnackAction) => {
            const text = upperFirstLetter(message.text);
            const duration = message.persistent ? Infinity : 5000;

            lastPushId = toastFnForVariant(message.variant)(text, {
                duration,
                dismissible: true,
                ...(message.description && { description: upperFirstLetter(message.description) }),
                ...(action && {
                    action: {
                        label: (
                            <div className="flex items-center justify-center size-2 text-text-inverse hover:bg-fill-x60">
                                <SizedIcon icon={ArrowForwardIcon} size={16} titleAccess="View" />
                            </div>
                        ),
                        onClick: () => action.onClick(),
                    },
                }),
            });

            if (message.variant === 'negative') {
                sendAmplitudeEvent('error_shown', {
                    message: message.text,
                    page_type: typeof window !== 'undefined' ? window.location.pathname : '',
                });
            }

            return lastPushId;
        },

        popMessage: (toastId?: string | number) => {
            const id = toastId ?? lastPushId;
            toast.dismiss(id);
            if (id === lastPushId) {
                lastPushId = undefined;
            }
        },
    },
}));

export function handleSnackbarError(serverError: ServerError): void {
    if (serverError.cancelled) {
        return;
    }

    const neutral = serverError.unavailable || serverError.notFound;
    let errorString = '';
    try {
        errorString = JSON.stringify(serverError);
    } catch {
        const seen = new WeakSet();
        errorString = JSON.stringify(serverError, (_key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[Circular]';
                }
                seen.add(value);
            }
            return value;
        });
    }
    if (!isSuppressedApiError(serverError.error?.code)) {
        logProviderFactory.logError(`UI handle error - ${errorString}`);
    }
    useSnackbarStore.getState().actions.pushMessage({
        ...serverErrorSnack(serverError),
        variant: neutral ? 'neutral' : 'negative',
    });
}
